import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocalSearchParams, useRouter } from 'expo-router';
import React, { useEffect, useMemo, useState, type ReactNode } from 'react';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { headerItem } from '@/components/HeaderActions';
import { Header } from '@/components/header';
import { Box } from '@/components/ui/box';
import { Spinner } from '@/components/ui/spinner';
import { VStack } from '@/components/ui/vstack';
import { ROUTES } from '@/constants/routes';
import {
  SurveyResults,
  getSurveyResultsQueryOptions,
  isSurveyResultsNotFoundError,
} from '@/features/polls';
import { SurveyForm, SurveyOutcomeMessage, useSurveyScrollView } from '@/features/surveys';
import {
  getCurrentSurveyByGroupQueryOptions,
  getMySurveyResponsesV2QueryOptions,
  hasRespondedToSurvey,
  submitSurveyResponse,
  type SurveyCompletion,
} from '@/features/surveys';
import {
  parseSurveyCompletion,
  getHiddenSurveyElementNames,
  parseSurveyPages,
  resolveSurveyCompletionHtml,
  SUPPORTED_SURVEY_ELEMENT_TYPES,
} from '@/features/surveys/model';
import { useSurveyVisitsStore, clearSurveyDraft } from '@/features/surveys/state';
import { useToaster } from '@/hooks/useToaster';
import { useUserScope } from '@/hooks/useUserScope';
import { useUserScopedKey, useUserScopedQuery } from '@/hooks/useUserScopedQuery';
import { useGoBack } from '@/hooks/useGoBack';
import { Sentry } from '@/util/sentry';

/**
 * Full-screen modal that lets the current user answer a survey. The route's
 * `id` param is a survey *group* id, not a specific definition version —
 * resolved to whichever version is currently published via
 * `getCurrentSurveyByGroupQueryOptions`, so callers (and the hardcoded
 * `EXPO_PUBLIC_ONBOARDING_SURVEY_GROUP_ID` env var) can hold a stable
 * reference that keeps working across publishes instead of needing an
 * update every time a new version ships. Response checks and submission
 * still use the resolved definition's own `id`, since responses and answers
 * are tied to a specific version. Re-checks the caller's response list on
 * mount (not just at the trigger that navigated here) since submission has
 * no server-side dedupe — see design.md's "no de-dupe assumption on the
 * backend" decision.
 *
 * A `?preview=true` search param (used by the feature-flagged "Preview
 * onboarding survey" entry in Account settings) bypasses the
 * already-responded gate so the form always renders even for a user who
 * has already responded. Submission is allowed in preview too — the survey
 * response API has no update endpoint, so submitting from preview after an
 * existing response creates an additional response row rather than
 * updating one. Preview also saves no draft, for the same reason it records
 * no visit: whoever is inspecting a survey is not a respondent partway
 * through one, and their poking about should not come back as saved answers.
 */
export default function SurveyScreen() {
  const { id: surveyGroupId, preview } = useLocalSearchParams<{ id: string; preview?: string }>();

  const isPreview = preview === 'true';

  const { userId, canFetch } = useUserScope();

  const responsesKey = useUserScopedKey(getMySurveyResponsesV2QueryOptions);

  const markVisited = useSurveyVisitsStore((state) => state.markVisited);

  const router = useRouter();

  const goBack = useGoBack(ROUTES.ACCOUNT);

  const queryClient = useQueryClient();

  const { bottom } = useSafeAreaInsets();

  const toaster = useToaster();

  const [submitted, setSubmitted] = useState(false);

  /**
   * The survey's own thank-you, resolved from the answers actually sent.
   *
   * Held from submit time rather than recomputed on render, because the form
   * that produced those answers is gone by the time this shows.
   */
  const [completionHtml, setCompletionHtml] = useState<string | undefined>(undefined);

  /**
   * What the API said on accepting the response: the thank-you for this
   * survey's type, and whether this respondent may read the poll's results.
   */
  const [completion, setCompletion] = useState<SurveyCompletion | undefined>(undefined);

  const [submittedAnswers, setSubmittedAnswers] = useState<Record<string, unknown> | undefined>(
    undefined
  );

  const {
    data: survey,
    isLoading: isSurveyLoading,
    isError: isSurveyError,
    error: surveyError,
  } = useQuery(getCurrentSurveyByGroupQueryOptions(surveyGroupId));

  const { data: responses, isLoading: isResponsesLoading } = useUserScopedQuery(
    getMySurveyResponsesV2QueryOptions,
    []
  );

  /**
   * Held stable so the form's schema and visibility memos actually cache — they
   * key on the element array, and parsing inline would hand them a fresh one on
   * every render of this screen.
   */
  const surveyPages = useMemo(() => parseSurveyPages(survey?.schema), [survey?.schema]);

  /**
   * The questions this respondent was actually asked. Results merge every
   * revision of a poll and count every question type, while the form shows only
   * the current version's questions of a type it can render, so without this the
   * results could list a question the respondent never saw.
   */
  const surveyQuestionNames = useMemo(
    () =>
      new Set(
        surveyPages.flatMap((page) =>
          page.elements
            .filter((element) =>
              SUPPORTED_SURVEY_ELEMENT_TYPES.includes(
                element.type as (typeof SUPPORTED_SURVEY_ELEMENT_TYPES)[number]
              )
            )
            .map((element) => element.name)
        )
      ),
    [surveyPages]
  );

  /**
   * The form asks to scroll to the top when it moves between pages and when
   * validation blocks, because both replace what matters without moving the
   * scroll position — leaving the respondent mid-question on the next page, or
   * staring past an error summary they never see. The form decides when; the
   * scroll view belongs to this screen.
   */
  const { ScrollViewComponent, scrollToTop } = useSurveyScrollView();

  const { mutateAsync: submit, isPending: isSubmitting } = useMutation({
    mutationFn: (answers: Record<string, unknown>) => {
      if (!survey) {
        return Promise.reject(new Error('Survey not loaded'));
      }

      return submitSurveyResponse(survey.id, answers);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: responsesKey(),
      }),
  });

  const alreadyResponded = !!responses && !!survey && hasRespondedToSurvey(responses, survey.id);

  /**
   * The answers to mark as the respondent's own in the results. After a submit
   * they are the answers just sent. A returning respondent's come from their
   * newest response to this version, since a preview re-submission can leave
   * more than one.
   */
  const myAnswers = useMemo(() => {
    if (submitted) {
      return submittedAnswers;
    }

    if (!survey || !responses) {
      return undefined;
    }

    return responses
      .filter((response) => response.surveyDefinitionId === survey.id)
      .sort((first, second) => second.submittedAt.localeCompare(first.submittedAt))[0]?.answers;
  }, [submitted, submittedAnswers, survey, responses]);

  /**
   * The questions this respondent was actually shown. A question a show-if rule
   * hid, given their answers, was never on their screen, so it is left out of
   * the results the same way the form left it out. Without answers to evaluate
   * the rules against, every question the form can render stays in.
   */
  const shownQuestionNames = useMemo(() => {
    if (!myAnswers) {
      return surveyQuestionNames;
    }

    const hiddenNames = getHiddenSurveyElementNames(
      surveyPages.flatMap((page) => page.elements),
      myAnswers
    );

    return new Set([...surveyQuestionNames].filter((name) => !hiddenNames.has(name)));
  }, [myAnswers, surveyPages, surveyQuestionNames]);

  /**
   * Results are fetched only when the API has said this respondent may read
   * them. After a submit, that is the results link it returned. A returning
   * respondent has no submit response to consult, so a poll they already
   * answered is asked for directly and the API's 404 decides. Preview never
   * takes the returning path, because it shows the form rather than an outcome.
   */
  const shouldFetchResults = submitted
    ? !!completion?.resultsUrl
    : alreadyResponded && !isPreview && survey?.type === 'poll';

  const resultsQueryOptions = getSurveyResultsQueryOptions(survey?.surveyGroupId);

  const {
    data: results,
    isLoading: isResultsLoading,
    isError: isResultsError,
    error: resultsError,
  } = useQuery({
    ...resultsQueryOptions,
    enabled: resultsQueryOptions.enabled !== false && shouldFetchResults,
  });

  useEffect(() => {
    if (!isResultsError || isSurveyResultsNotFoundError(resultsError)) {
      return;
    }

    Sentry.captureException(resultsError, {
      tags: { component: 'SurveyScreen', source: 'survey-results' },
      extra: {
        surveyGroupId,
        errorMessage: resultsError instanceof Error ? resultsError.message : String(resultsError),
      },
    });
  }, [isResultsError, resultsError, surveyGroupId]);

  useEffect(() => {
    if (!isSurveyError) {
      return;
    }

    Sentry.captureException(surveyError, {
      tags: { component: 'SurveyScreen', source: 'survey' },
      extra: {
        surveyGroupId,
        errorMessage: surveyError instanceof Error ? surveyError.message : String(surveyError),
      },
    });
  }, [isSurveyError, surveyError, surveyGroupId]);

  /**
   * Marks this survey as visited so `useResumableSurveys` can offer a way
   * back to it if the user leaves without completing it — a plain
   * "unanswered" check alone would also surface surveys never opened.
   *
   * Preview is excluded: it is a staff tool for inspecting a survey, not a
   * user intending to answer one, and recording it would put a resume row
   * and a badge in front of whoever previewed.
   *
   * Waiting for the survey to resolve keeps unroutable ids out of storage.
   * Callers can navigate here with a placeholder when their configuration is
   * missing, and such an id would otherwise be refetched every session until
   * it expired, never rendering a row.
   */
  useEffect(() => {
    if (!surveyGroupId || !userId || !canFetch || isPreview || !survey) {
      return;
    }

    markVisited(userId, surveyGroupId).catch(() => {});
  }, [surveyGroupId, userId, canFetch, isPreview, survey, markVisited]);

  const handleSubmit = async (answers: Record<string, unknown>) => {
    try {
      const submittedResponse = await submit(answers);

      // Only once the response is accepted — a draft is what the user falls
      // back to when a submit fails, so discarding it any earlier would
      // destroy their answers at the exact moment they still need them.
      //
      // Preview is held to the same rule as saving rather than just skipping
      // the write: it never created this draft, so it must not delete the one
      // a real attempt at the survey left behind.
      if (!isPreview) {
        await clearSurveyDraft(userId, surveyGroupId);
      }

      setCompletionHtml(
        resolveSurveyCompletionHtml(parseSurveyCompletion(survey?.schema), answers)
      );

      setCompletion(submittedResponse?.completion);

      setSubmittedAnswers(answers);

      setSubmitted(true);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'SurveyScreen', source: 'survey' },
        extra: {
          surveyGroupId,
          surveyDefinitionId: survey?.id,
          errorMessage: error instanceof Error ? error.message : String(error),
        },
      });

      toaster.showError({
        title: 'Unable to submit your response',
        apiError: { error },
      });
    }
  };

  const handleDone = () => {
    if (router.canDismiss()) {
      router.dismiss();
    } else {
      // With nothing behind it, as after a deep link from a cold start, Back
      // replaces this route with Account rather than pushing. This route is a
      // modal, and native-stack gives every route stacked above a modal one the
      // same treatment, so a push would leave the whole app in a sheet sitting
      // on top of the survey the user was trying to leave.
      goBack();
    }
  };

  const isLoading = isSurveyLoading || isResponsesLoading;

  /**
   * Whether the body is an end state rather than the form. The header's exit
   * reads "Close" there: once a response is in there is nothing left to cancel,
   * and "Cancel" beside a thank-you reads as withdrawing the answer.
   */
  const isShowingOutcome =
    !isLoading && (submitted || (alreadyResponded && !isPreview) || isSurveyError);

  let resultsContent: ReactNode = null;

  if (shouldFetchResults && isResultsLoading) {
    resultsContent = <Spinner size="small" />;
  } else if (shouldFetchResults && results) {
    resultsContent = (
      <SurveyResults results={results} answers={myAnswers} questionNames={shownQuestionNames} />
    );
  }

  let body: ReactNode = null;

  /** Only the form scrolls. Its end states are short and sit below the bar. */
  let bodyScrollsUnderHeader = false;

  if (isLoading) {
    body = <Spinner size="large" className="flex-1" />;
  } else if (submitted) {
    body = (
      <SurveyOutcomeMessage
        message={completion?.message ?? 'Thanks for your feedback!'}
        html={completionHtml}
        onDone={handleDone}>
        {resultsContent}
      </SurveyOutcomeMessage>
    );
  } else if (alreadyResponded && !isPreview) {
    body = (
      <SurveyOutcomeMessage message="You've already responded to this survey." onDone={handleDone}>
        {resultsContent}
      </SurveyOutcomeMessage>
    );
  } else if (isSurveyError) {
    body = (
      <SurveyOutcomeMessage message="This survey isn't available right now." onDone={handleDone} />
    );
  } else if (survey) {
    bodyScrollsUnderHeader = true;

    body = (
      <KeyboardAwareScrollView
        mode="layout"
        contentInsetAdjustmentBehavior="automatic"
        ScrollViewComponent={ScrollViewComponent}
        className="flex-1"
        contentContainerStyle={{ paddingBottom: 32 }}
        /*
         * Deliberately stricter than the rest of the app, which persists taps.
         *
         * A tap that lands outside the focused field dismisses the keyboard and
         * stops there, rather than also being delivered to whatever it landed
         * on. A survey page is mostly radio and checkbox rows sized to the 48px
         * touch-target minimum, so their pressable area reaches into what looks
         * like blank space; persisting the tap let a respondent putting the
         * keyboard away silently overwrite an answer they had already given.
         *
         * The cost is one extra tap to answer a question straight after typing.
         * That is the right trade here: these answers are analyzed and the
         * respondent is paid for them, so a wrong one is worse than a slow one.
         */
        keyboardShouldPersistTaps="never"
        keyboardDismissMode="on-drag"
        bottomOffset={18}>
        <VStack className="mx-auto w-full max-w-screen-md px-6 pt-2" space="md">
          <SurveyForm
            surveyId={survey.id}
            surveyName={survey.name ?? ''}
            pages={surveyPages}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onScrollToTop={scrollToTop}
            surveyGroupId={isPreview ? undefined : surveyGroupId}
            userId={userId}
            surveyType={survey.type}
          />
        </VStack>
      </KeyboardAwareScrollView>
    );
  }

  return (
    <Box className="flex-1 bg-background-0" style={{ paddingBottom: bottom }}>
      <Header
        headerShown
        coversActingAsBanner
        contentScrollsUnderHeader={bodyScrollsUnderHeader}
        title={survey?.name ?? ''}
        leftItems={[
          isShowingOutcome ? headerItem.close(handleDone) : headerItem.cancel(handleDone),
        ]}
      />

      <VStack className="w-full flex-1">{body}</VStack>
    </Box>
  );
}
